/**
 * Date formatting utilities for Irish timezone (Europe/Dublin)
 * Uses European format: DD/MM/YYYY and 24-hour time
 */

const IRISH_TIMEZONE = 'Europe/Dublin';

/**
 * Format date to European format DD/MM/YYYY
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IE', {
    timeZone: IRISH_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Format date with full month name: DD Month YYYY
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateLong = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IE', {
    timeZone: IRISH_TIMEZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Format time to 24-hour format HH:MM
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Formatted time string
 */
export const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-IE', {
    timeZone: IRISH_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format date and time: DD/MM/YYYY HH:MM
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-IE', {
    timeZone: IRISH_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format relative time (e.g., "2 hours ago", "3 days ago")
 * Falls back to full date after 7 days
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Relative or formatted date string
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  
  return formatDate(dateString);
};

/**
 * Get current date in YYYY-MM-DD format for input[type="date"]
 * @returns {string} Current date in YYYY-MM-DD format
 */
export const getCurrentDateForInput = () => {
  const now = new Date();
  // Get date in Irish timezone
  const options = { timeZone: IRISH_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' };
  const parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(now);
  const year = parts.find(p => p.type === 'year').value;
  const month = parts.find(p => p.type === 'month').value;
  const day = parts.find(p => p.type === 'day').value;
  return `${year}-${month}-${day}`;
};

/**
 * Format UPS delivery date from YYYYMMDD to "4th of April, 2026"
 * @param {string} dateStr - Date in YYYYMMDD format
 * @returns {string} Formatted date string
 */
export const formatDeliveryDate = (dateStr) => {
  if (!dateStr) return '';
  const str = String(dateStr).replace(/[^0-9]/g, '');
  if (str.length < 8) return dateStr;
  const year = parseInt(str.substring(0, 4));
  const month = parseInt(str.substring(4, 6)) - 1;
  const day = parseInt(str.substring(6, 8));
  const date = new Date(year, month, day);
  if (isNaN(date.getTime())) return dateStr;
  const ordinal = (d) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
  };
  const monthName = date.toLocaleDateString('en-IE', { month: 'long' });
  return `${day}${ordinal(day)} of ${monthName}, ${year}`;
};
